/**
 * Notifications Page Skeleton
 * Matches the exact layout of the notifications settings page
 *
 * Layout:
 * - Container: h-full overflow-y-auto > p-6 w-full max-w-4xl mx-auto pb-24
 * - Header: mb-8 with "Notifications" (text-2xl) + subtitle
 * - Master Switch Card: bg-card-bg rounded-lg shadow-sm border mb-8
 *   - Header: px-6 py-5 border-b bg-card-header with title + toggle
 *   - Content: px-6 py-6 with icon + status text + description
 * - Categories Card: same card structure
 *   - Header: title + description
 *   - Categories: space-y-6 with icon + title + description + channel toggles
 * - Push Devices Card: same card structure
 * - Save Button: flex justify-end with px-6 py-3
 */

import React from 'react';
import { Skeleton } from './Skeleton';

/**
 * Card header skeleton
 * Matches the card header with optional toggle
 */
const CardHeaderSkeleton: React.FC<{
  hasToggle?: boolean;
  hasDescription?: boolean;
}> = ({ hasToggle = false, hasDescription = false }) => (
  <div className="px-6 py-5 border-b border-border bg-card-header">
    <div className="flex justify-between items-center">
      <Skeleton className="h-6 w-44" /> {/* Title */}
      {hasToggle && <Skeleton className="h-6 w-11 rounded-full" />}
    </div>
    {hasDescription && <Skeleton className="h-4 w-72 mt-1" />}
  </div>
);

/**
 * Master switch content skeleton
 * Matches the notification enabled/disabled status display
 */
const MasterSwitchContentSkeleton: React.FC = () => (
  <div className="px-6 py-6">
    <div className="flex items-center">
      <Skeleton className="w-5 h-5 rounded mr-3" /> {/* Bell icon */}
      <div>
        <Skeleton className="h-5 w-48 mb-1" /> {/* Status text */}
        <Skeleton className="h-4 w-80" /> {/* Description */}
      </div>
    </div>
  </div>
);

/**
 * Notification category skeleton
 * Matches the category row with icon, title, description, and channel toggles
 */
const CategorySkeleton: React.FC<{ isLast?: boolean }> = ({ isLast = false }) => (
  <div className={`${!isLast ? 'border-b border-border pb-6' : ''}`}>
    {/* Category header */}
    <div className="flex items-center mb-4">
      <Skeleton className="w-10 h-10 rounded-lg mr-4" /> {/* Icon container */}
      <div className="flex-1">
        <Skeleton className="h-5 w-40 mb-1" /> {/* Title */}
        <Skeleton className="h-4 w-72" /> {/* Description */}
      </div>
    </div>

    {/* Channel toggles */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ml-14">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between p-3 bg-card-header rounded-lg">
          <div className="flex items-center space-x-2">
            <Skeleton className="w-4 h-4 rounded" /> {/* Channel icon */}
            <Skeleton className="h-4 w-12" /> {/* Channel name */}
          </div>
          <Skeleton className="h-5 w-9 rounded-full" /> {/* Toggle */}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Push device item skeleton
 * Matches the registered device row
 */
const DeviceItemSkeleton: React.FC = () => (
  <div className="p-3 bg-card-header rounded-lg flex items-center justify-between">
    <div>
      <Skeleton className="h-4 w-28 mb-1" /> {/* Device name */}
      <Skeleton className="h-3 w-44" /> {/* Browser + last used */}
    </div>
    <Skeleton className="h-4 w-14" /> {/* Remove button */}
  </div>
);

/**
 * Full notifications page skeleton
 * Use this as the loading state for the notifications page
 */
export const NotificationsSkeleton: React.FC = () => (
  <div className="h-full overflow-y-auto">
    <div className="p-6 w-full max-w-4xl mx-auto pb-24">
      {/* Page Header */}
      <div className="mb-8">
        <Skeleton className="h-8 w-36 mb-2" /> {/* "Notifications" */}
        <Skeleton className="h-4 w-72" /> {/* Subtitle */}
      </div>

      {/* Master Switch Card */}
      <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
        <CardHeaderSkeleton hasToggle />
        <MasterSwitchContentSkeleton />
      </div>

      {/* Notification Categories Card */}
      <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
        <CardHeaderSkeleton hasDescription />
        <div className="px-6 py-6">
          <div className="space-y-6">
            <CategorySkeleton />
            <CategorySkeleton />
            <CategorySkeleton />
            <CategorySkeleton />
            <CategorySkeleton />
            <CategorySkeleton isLast />
          </div>
        </div>
      </div>

      {/* Push Notification Devices Card */}
      <div className="bg-card-bg rounded-lg shadow-sm border border-border overflow-hidden mb-8">
        <CardHeaderSkeleton hasDescription />
        <div className="px-6 py-6">
          {/* Current browser status */}
          <div className="p-4 bg-card-header rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <Skeleton className="h-4 w-28 mb-1" /> {/* "Current Browser" */}
                <Skeleton className="h-3 w-56" /> {/* Status text */}
              </div>
              <Skeleton className="h-5 w-9 rounded-full" /> {/* Toggle */}
            </div>
          </div>

          {/* Registered devices */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-36 mb-2" /> {/* "Registered Devices" */}
            <DeviceItemSkeleton />
            <DeviceItemSkeleton />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Skeleton className="h-12 w-36 rounded-lg" />
      </div>
    </div>
  </div>
);

export default NotificationsSkeleton;
